import { pandaFaceSVG } from './pandaFace';

// In-game HUD (§11 responsive HUD). Pure DOM over the canvas, laid out for
// landscape and clear of safe areas (CSS).

export class HUD {
  private root: HTMLElement;
  private scoreEl: HTMLElement;
  private comboEl: HTMLElement;
  private comboMeter: HTMLElement;
  private timeEl: HTMLElement;
  private surfaceEl: HTMLElement;
  private promptEl: HTMLElement;
  private popupLayer: HTMLElement;
  private speedFx: HTMLElement;
  private moodFill: HTMLElement;

  constructor(parent: HTMLElement) {
    this.root = document.createElement('div');
    this.root.className = 'hud hidden';
    this.root.innerHTML = `
      <div class="hud-mood" aria-label="Panda mood">
        <span class="mood-face" id="mood-face-sad"></span>
        <div class="mood-bar"><i id="hud-mood-fill"></i></div>
        <span class="mood-face" id="mood-face-happy"></span>
      </div>
      <div class="hud-top-left">
        <div class="hud-score" id="hud-score">0</div>
        <div class="hud-combo" id="hud-combo">x1</div>
        <div class="hud-combo-meter"><i id="hud-combo-meter"></i></div>
      </div>
      <div class="hud-top-right">
        <div class="hud-time" id="hud-time">0.0s</div>
        <div class="hud-surface" id="hud-surface">Grass</div>
      </div>
      <div class="hud-prompt" id="hud-prompt"></div>
      <div class="popup-layer" id="hud-popups"></div>
    `;
    parent.appendChild(this.root);

    this.speedFx = document.createElement('div');
    this.speedFx.className = 'speed-fx';
    parent.appendChild(this.speedFx);

    this.scoreEl = this.q('hud-score');
    this.comboEl = this.q('hud-combo');
    this.comboMeter = this.q('hud-combo-meter');
    this.timeEl = this.q('hud-time');
    this.surfaceEl = this.q('hud-surface');
    this.promptEl = this.q('hud-prompt');
    this.popupLayer = this.q('hud-popups');
    this.moodFill = this.q('hud-mood-fill');
    this.q('mood-face-sad').innerHTML = pandaFaceSVG(0, 28);
    this.q('mood-face-happy').innerHTML = pandaFaceSVG(1, 28);
  }

  /** mood: 0 (sad) .. 1 (happy). Fill grows with mood; colour goes red->green. */
  setMood(mood: number): void {
    const m = Math.max(0, Math.min(1, mood));
    this.moodFill.style.width = `${(m * 100).toFixed(1)}%`;
    // Hue 0 (red) at empty -> 120 (green) when full.
    this.moodFill.style.background = `hsl(${(m * 120).toFixed(0)}, 78%, 47%)`;
  }

  private q(id: string): HTMLElement {
    return this.root.querySelector(`#${id}`) as HTMLElement;
  }

  show(): void {
    this.root.classList.remove('hidden');
  }

  hide(): void {
    this.root.classList.add('hidden');
    this.speedFx.style.opacity = '0';
  }

  setScore(n: number): void {
    this.scoreEl.textContent = n.toLocaleString();
  }

  setTime(sec: number): void {
    this.timeEl.textContent = `${sec.toFixed(1)}s`;
  }

  setCombo(streak: number, multiplier: number, fraction: number): void {
    if (streak > 1) {
      this.comboEl.classList.add('active');
      this.comboEl.textContent = `x${multiplier.toFixed(2).replace(/\.00$/, '')} · ${streak} chain`;
    } else {
      this.comboEl.classList.remove('active');
    }
    this.comboMeter.style.transform = `scaleX(${fraction})`;
  }

  setSurface(label: string): void {
    this.surfaceEl.textContent = label;
  }

  setPrompt(text: string): void {
    this.promptEl.textContent = text;
  }

  setSpeedFx(speed01: number): void {
    // Motion lines + vignette ramp in over the top half of the speed range.
    const o = Math.max(0, (speed01 - 0.45) / 0.55) * 0.7;
    this.speedFx.style.opacity = o.toFixed(3);
  }

  popup(text: string, color: string, xPct: number, yPct: number): void {
    const el = document.createElement('div');
    el.className = 'popup';
    el.textContent = text;
    el.style.color = color;
    el.style.left = `${xPct}%`;
    el.style.top = `${yPct}%`;
    this.popupLayer.appendChild(el);
    window.setTimeout(() => el.remove(), 950);
  }
}
